import React, { useEffect, useRef } from 'react';

export const ParticleBackground = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef();
  const particlesRef = useRef([]);

  const createParticle = (canvas) => {
    const particle = {
      x: 0,
      y: 0,
      speed: 0,
      opacity: 1,
      fadeDelay: 0,
      fadeStart: 0,
      fadingOut: false,
      color: Math.random() > 0.5 ? '#0DA2E7' : '#F97316', // Blue or Orange
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.speed = Math.random() / 5 + 0.1;
        this.opacity = 1;
        this.fadeDelay = Math.random() * 600 + 100;
        this.fadeStart = Date.now() + this.fadeDelay;
        this.fadingOut = false;
      },
      update() {
        this.y -= this.speed;
        if (this.y < 0) {
          this.reset();
        }

        if (!this.fadingOut && Date.now() > this.fadeStart) {
          this.fadingOut = true;
        }

        if (this.fadingOut) {
          this.opacity -= 0.008;
          if (this.opacity <= 0) {
            this.reset();
          }
        }
      },
      draw(ctx) {
        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.opacity;
        ctx.fillRect(this.x, this.y, Math.random() * 2 + 1, Math.random() * 2 + 1);
        ctx.globalAlpha = 1;
      },
    };

    particle.reset();
    particle.y = Math.random() * canvas.height;
    particle.fadeDelay = Math.random() * 600 + 100;
    particle.fadeStart = Date.now() + particle.fadeDelay;
    particle.fadingOut = false;

    return particle;
  };

  const calculateParticleCount = (canvas) => {
    return Math.floor((canvas.width * canvas.height) / 4000); // slightly more dense
  };

  const initParticles = (canvas) => {
    const particleCount = calculateParticleCount(canvas);
    particlesRef.current = [];
    for (let i = 0; i < particleCount; i++) {
      particlesRef.current.push(createParticle(canvas));
    }
  };

  const animate = (canvas, ctx) => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    particlesRef.current.forEach((particle) => {
      particle.update();
      particle.draw(ctx);
    });
    animationRef.current = requestAnimationFrame(() => animate(canvas, ctx));
  };

  const handleResize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    initParticles(canvas);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    initParticles(canvas);
    animate(canvas, ctx);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden z-0 pointer-events-none">
      <style>{`
        @keyframes spotlight {
          0% { transform: rotateZ(0deg) scale(1); filter: blur(20px) opacity(0.3); }
          20% { transform: rotateZ(-1deg) scale(1.2); filter: blur(25px) opacity(0.4); }
          40% { transform: rotateZ(2deg) scale(1.3); filter: blur(15px) opacity(0.2); }
          60% { transform: rotateZ(-2deg) scale(1.2); filter: blur(25px) opacity(0.4); }
          80% { transform: rotateZ(1deg) scale(1.1); filter: blur(20px) opacity(0.2); }
          100% { transform: rotateZ(0deg) scale(1); filter: blur(20px) opacity(0.3); }
        }
        @keyframes loadrot {
          0% { transform: rotate(0deg) scale(0);}
          100% { transform: scale(1);}
        }
        @keyframes accentload {
          0% { opacity: 0; transform: scale(0); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>

      {/* Spotlights */}
      <div className="absolute left-0 right-0 top-0 mx-auto w-full h-full overflow-hidden opacity-60">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              borderRadius: '0 0 50% 50%',
              position: 'absolute',
              left: 0,
              right: 0,
              margin: '0 auto',
              top: '-10em',
              width: '40em',
              height: 'max(42em, 86vh)',
              backgroundImage:
                i % 2 === 0
                  ? 'conic-gradient(from 0deg at 50% -5%, transparent 45%, rgba(13, 162, 231, .3) 49%, rgba(13, 162, 231, .5) 50%, rgba(13, 162, 231, .3) 51%, transparent 55%)' // Blue
                  : 'conic-gradient(from 0deg at 50% -5%, transparent 45%, rgba(249, 115, 22, .2) 49%, rgba(249, 115, 22, .4) 50%, rgba(249, 115, 22, .2) 51%, transparent 55%)', // Orange
              transformOrigin: '50% 0',
              filter: 'blur(20px) opacity(0.4)',
              zIndex: -1,
              transform: i === 0 ? 'rotate(20deg)' : i === 1 ? 'rotate(-20deg)' : 'rotate(0deg)',
              animation:
                i === 0
                  ? 'loadrot 2s ease-in-out forwards, spotlight 17s ease-in-out infinite'
                  : i === 1
                    ? 'loadrot 2s ease-in-out forwards, spotlight 14s ease-in-out infinite'
                    : 'loadrot 2s ease-in-out forwards, spotlight 21s ease-in-out infinite reverse',
            }}
          />
        ))}
      </div>



      {/* Canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1,
          width: '100%',
          height: '100%',
          opacity: 0.8,
        }}
      />
    </div>
  );
};
